import { Component, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { HeaderService } from '../../../../services/header/header.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs/operators';
import { Fournisseur } from '../../../../models/fournisseur';
import { FournisseurService } from '../../../../services/Fournisseur/fournisseur.service';
import { FournisseurEventService } from '../../../../services/Fournisseur/fournisseur-event.service'; // Add this import
import * as Feather from 'feather-icons';

@Component({
    selector: 'app-details-fourniss',
    templateUrl: './details-fourniss.component.html',
    styleUrls: ['./details-fourniss.component.scss']
})
export class DetailsFournissComponent implements OnInit, OnDestroy {
    @ViewChild('cashAdvanceModal') cashAdvanceModal!: TemplateRef<any>;
    fournisseur: Fournisseur | null = null;
    isLoading: boolean = false;
    error: string | null = null;
    stationId: number = 0;
    cashAdvance: { montant: number | null; note: string } = { montant: null, note: '' };
    private subscriptions: Subscription[] = [];
    private navigationState: any = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private fournisseurService: FournisseurService,
        private headerService: HeaderService,
        private modalService: NgbModal,
        private fournisseurEventService: FournisseurEventService // Inject the service
    ) {
        this.subscriptions.push(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => {
                    const navigation = this.router.getCurrentNavigation();
                    this.navigationState = navigation?.extras.state;
                })
        );
    }

    ngOnInit(): void {
        this.headerService.setConfig({
            title: 'Fournisseur',
            buttons: [
                {
                    label: 'Modifier',
                    type: 'secondary'
                }
            ]
        });
        this.loadUserData();

        let fournisseurId: string | null = this.route.snapshot.paramMap.get('reference');

        if (!fournisseurId && this.navigationState) {
            const state = this.navigationState as { fournisseur: Fournisseur } | undefined;
            fournisseurId = state?.fournisseur?.id?.toString() || state?.fournisseur?.identifiant_unique || null;
        }

        if (!fournisseurId) {
            this.error = 'Aucun ID de fournisseur fourni dans la route ou l\'état de navigation.';
            this.toastr.error(this.error, 'Erreur');
            this.router.navigate(['/tiers/fournisseurs']);
            return;
        }

        const parsedId = typeof fournisseurId === 'string' && !isNaN(parseInt(fournisseurId, 10)) ? parseInt(fournisseurId, 10) : fournisseurId;
        if (typeof parsedId === 'string' && !parsedId) {
            this.error = `ID de fournisseur invalide: ${fournisseurId}`;
            this.toastr.error(this.error, 'Erreur');
            this.router.navigate(['/tiers/fournisseurs']);
            return;
        }

        // Subscribe to fournisseur updates
        this.subscriptions.push(
            this.fournisseurEventService.getFournisseur().subscribe(fournisseur => {
                if (fournisseur && fournisseur.id === this.fournisseur?.id) {
                    this.fournisseur = { ...fournisseur }; // Update fournisseur
                    this.updateHeaderConfig(); // Update header with new fournisseur data
                    setTimeout(() => Feather.replace(), 0);
                }
            })
        );

        this.loadFournisseurData(parsedId);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    private loadUserData(): void {
        try {
            const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
            this.stationId = user?.stations?.[0]?.id || 0;
            if (!this.stationId) {
                this.error = 'Aucune station trouvée. Veuillez créer une station.';
                this.toastr.error(this.error, 'Erreur');
                this.router.navigate(['/create-station']);
            }
        } catch (error) {
            this.error = 'Erreur lors de la récupération des données utilisateur.';
            this.toastr.error(this.error, 'Erreur');
            this.stationId = 0;
        }
    }

    private loadFournisseurData(fournisseurId: number | string): void {
        this.isLoading = true;
        const sub = this.fournisseurService.getFournisseurById(fournisseurId).subscribe({
            next: (fournisseurData: Fournisseur) => {
                if (fournisseurData.station_id !== this.stationId) {
                    this.error = 'Le fournisseur appartient à une station différente.';
                    this.toastr.error(this.error, 'Erreur');
                    this.router.navigate(['/tiers/fournisseurs']);
                    return;
                }
                this.fournisseur = fournisseurData;
                this.fournisseurEventService.updateFournisseur(fournisseurData); // Initialize the BehaviorSubject
                this.updateHeaderConfig();
                this.isLoading = false;
                setTimeout(() => Feather.replace(), 0);
            },
            error: (error) => {
                this.isLoading = false;
                this.error = error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du chargement des données du fournisseur';
                this.toastr.error(this.error, 'Erreur');
                this.router.navigate(['/tiers/fournisseurs']);
            },
        });
        this.subscriptions.push(sub);
    }

    private updateHeaderConfig(): void {
        this.headerService.setConfig({
            title: `Détails du fournisseur: ${this.fournisseur?.nom_entreprise || 'N/A'}`,
            buttons: [

                {
                    label: 'Modifier',
                    type: 'secondary',
                    action: () => this.editFournisseur(this.fournisseur!),
                },
            ],
        });
    }

    editFournisseur(fournisseur: Fournisseur) {
        this.router.navigate([`/tiers/fournisseurs/edit/${fournisseur.id}`], {
            state: { fournisseur, isEditMode: true },
        });
    }

    handleCashAdvance(): void {
        if (!this.fournisseur) {
            this.toastr.error('Aucun fournisseur sélectionné.', 'Erreur');
            return;
        }
        this.cashAdvance = { montant: null, note: '' };
        this.modalService.open(this.cashAdvanceModal, { centered: true }).result.then(
            (result) => {
                if (result === 'Confirm') {
                    this.submitCashAdvance();
                }
            },
            () => {
                this.toastr.info('Avance en espèce annulée', 'Information');
            }
        );
    }

    submitCashAdvance(): void {
        if (!this.cashAdvance.montant || this.cashAdvance.montant <= 0) {
            this.toastr.error('Le montant doit être supérieur à 0.', 'Erreur');
            return;
        }

        this.toastr.success(`Avance en espèce de ${this.cashAdvance.montant} enregistrée pour ${this.fournisseur?.nom_entreprise}`, 'Succès');
        this.modalService.dismissAll();
    }
}
